import { useEffect, useMemo, useRef, useState } from 'react';
import ConciergeChat from '../components/ConciergeChat/ConciergeChat';
import { isConciergeChat } from '../services/concierge';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  confirmTransaction,
  declineTransaction,
  fetchCancelReasons,
  getTransaction,
  markReceiptIntent,
  processTransaction,
  retrySearch,
  selectVariant,
} from '../services/api';
import { resolveTemplate } from '../templates';
import { followsPaymentReview, paymentMethodFromLink, resolveTemplateName } from '../templates/templateNames';
import LionOnePageTemplate from '../templates/LionOnePageTemplate';
import PayPageGateV1 from '../templates/PayPageGateV1';
import {
  getStateDeadline,
  resolveTimeoutScreen,
  TimeoutScreen,
} from '../services/stateTimeout';
import { shouldWarnLowTime } from '../services/lowTimeWarning';
import { isCancelReasonOption, visibleCancelReasons } from '../services/cancelReasons';
import type { CancelReasonList, CancelReasonOption, DeclineContext } from '../services/types';
import { playAlertSound, primeAlertSound } from '../services/alertSound';
import {
  track,
  buildBasePayload,
  detectTemplate,
  setAnalyticsContext,
  initVisitOutcome,
  initBehaviour,
  markVisitAction,
} from '../analytics';
import { setDeviceIdFromCollection, getSessionId } from '../analytics/identity';
import {
  confirmWithReceipt,
  ReceiptUploadHandlers,
  ReceiptUploadResult,
} from '../services/receiptUpload';
import { collectDevice } from '../services/deviceFingerprint';

// CPAYMENT-1670: statuses the ticket passes through while a client-requested requisite search runs
const SEARCHING_STATUSES = ['WAITING_FOR_REQUISITE', 'ADAPTER_PENDING', 'CLIENT_PENDING'];

const POLL_INTERVAL_MS = 3000;
const REVIEW_POLL_INTERVAL_MS = 10_000;

const TransactionPage = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [timeoutScreen, setTimeoutScreen] = useState<TimeoutScreen>(null);
  const intervalRef = useRef<NodeJS.Timer | null>(null);
  const [retry, setRetry] = useState(0);
  const [transgranError, setTransgranError] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [variantSubmitting, setVariantSubmitting] = useState<boolean>(false);
  const [retrySearching, setRetrySearching] = useState<boolean>(false);
  const retryRoundRef = useRef<number | null>(null);
  const firedOnceRef = useRef<Set<string>>(new Set());
  // CPAYMENT-1846: одна отправка на чекаут провайдера за маунт страницы. Без этого рефа любое
  // повторное обновление transaction в том же маунте (например, ответ на автоматический
  // receipt-intent) повторно триггерит эффект ниже и, застав sessionStorage уже выставленным,
  // перебивает ещё не завершившуюся навигацию на paymentUrl редиректом на redirectUrl.
  const paymentUrlHandledRef = useRef(false);
  // CPAYMENT-1716: отметка «клиент пошёл грузить чек» ставится один раз за сессию страницы
  const receiptIntentSentRef = useRef<boolean>(false);
  const receiptIntentRestoredRef = useRef<boolean>(false);
  // CPAYMENT-1730: та же отметка глазами страницы. Ответ бэкенда может ещё не прийти или запрос
  // вовсе упасть — но клиент уже заявил, что платит, и уходить со страницы ему нужно осознанно.
  const [receiptIntentLocal, setReceiptIntentLocal] = useState<boolean>(false);
  // CPAYMENT-1731: попап «мало времени» открывается один раз за сессию страницы — закрывший его
  // клиент сделал выбор, и возвращать окно на каждом тике таймера значит спорить с ним.
  const [lowTimeWarning, setLowTimeWarning] = useState<boolean>(false);
  const lowTimeWarnedRef = useRef<boolean>(false);
  const { i18n } = useTranslation();
  const [cancelReasonList, setCancelReasonList] = useState<CancelReasonList | null>(null);

  // One request per visit: what the cancel survey offers does not change while the page is open,
  // and the revision it came with is sent back with the pick.
  useEffect(() => {
    if (!uuid) return;
    let alive = true;
    fetchCancelReasons(uuid).then((list) => {
      if (alive) setCancelReasonList(list);
    });
    return () => {
      alive = false;
    };
  }, [uuid]);

  const cancelReasons = useMemo(
    () => visibleCancelReasons(cancelReasonList?.items ?? [], (key) => i18n.exists(key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cancelReasonList, i18n.language],
  );

  const basePayload = useMemo(
    () => buildBasePayload(uuid, transaction),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uuid, transaction?.merchant, transaction?.paymentMethod],
  );

  // Шаблон нужен не только рендеру: от него зависит, какое окно оплаты просит `receipt-intent`
  // (у одностраничных оно короче — см. `handleShowConfirm`), поэтому считаем его один раз здесь.
  const templateMethod = transaction?.paymentMethod ?? paymentMethodFromLink();
  const Template = resolveTemplate(transaction?.merchant, templateMethod);
  // Окно оплаты у одностраничных экранов одинаковое: у клиента реквизит и чек на одном экране,
  // и времени ему нужно столько же, каким бы из них ни был шаблон.
  const isOnePageTemplate = Template === LionOnePageTemplate || Template === PayPageGateV1;
  // The template shows the review of the payment, so the page keeps reading the ticket through it.
  const followsReview = followsPaymentReview(resolveTemplateName(transaction?.merchant, templateMethod));

  // CPAYMENT-1717: сервисы вне React (копирование реквизита, исход визита) не видят стейт страницы,
  // а событие без мерчанта и метода нечем резать. Публикуем текущий payload для них.
  useEffect(() => {
    setAnalyticsContext(basePayload);
    // Мерчант известен только после загрузки транзакции, поэтому `page.viewed` его не несёт и
    // нести не может. Кладём его в scope, как только он появился: дальше его получают и остальные
    // события, и ошибки, и реплей. Верх воронки при этом всё равно считается из БД.
    const merchant = transaction?.merchant;
    if (!merchant) return;
    try {
      Sentry.getGlobalScope().setAttributes({
        merchant,
        template: detectTemplate(merchant, templateMethod),
      });
    } catch {
      // analytics must never break UX
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePayload, transaction?.merchant]);

  // Чем закончился визит: страницу скрыли, разрушили или вернули. Слушатели ставятся один раз на
  // жизнь страницы — отдельно от трекера, потому что на выгрузке его транспорт события теряет.
  useEffect(() => initVisitOutcome(), []);
  useEffect(() => initBehaviour(uuid), [uuid]);

  const trackOnce = (key: string, fire: () => void) => {
    if (firedOnceRef.current.has(key)) return;
    firedOnceRef.current.add(key);
    fire();
  };

  useEffect(() => {
    const clearExistingInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const startInterval = () => {
      clearExistingInterval();
      intervalRef.current = setInterval(async () => {
        await attemptProcessAndCheckStatus();
      }, 3000);
    };

    // A concierge deposit lives on its chat socket (ConciergeChat), never on this page's polling.
    const shouldPoll = () =>
      !transaction?.concierge &&
      (transaction?.status === 'WAITING_FOR_REQUISITE' ||
        transaction?.status === 'WAITING_FOR_VARIANT_SELECTION') &&
      !document.hidden;

    if (shouldPoll()) {
      startInterval();
    } else {
      clearExistingInterval();
    }

    if (!loading && transaction?.status === 'WAITING_FOR_ACTION') {
      clearExistingInterval();
      handleRedirect(transaction.redirectUrl);
    }

    const handleVisibilityChange = () => {
      if (shouldPoll()) {
        attemptProcessAndCheckStatus();
        startInterval();
      } else {
        clearExistingInterval();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearExistingInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, loading, uuid, retry]);

  useEffect(() => {
    if (uuid) {
      (window as any).__b?.('5-page-effect');
      try {
        Sentry.getGlobalScope().setAttributes({ session_id: getSessionId(uuid) });
      } catch {
        // analytics must never break UX
      }
      trackOnce(`page.viewed:${uuid}`, () =>
        track({ name: 'page.viewed', payload: basePayload }),
      );
    }

    const collectAndFetchData = async () => {
      setLoading(true);
      try {
        (window as any).__b?.('6-before-fp');
        const device = await collectDevice();
        // The status suffix keeps `|= "7-after-fp"` queries working and makes coverage countable:
        // a bare beacon fires identically on success, timeout and a missing collector.
        (window as any).__b?.(`7-after-fp-${device.status}`);
        setDeviceIdFromCollection(device);
        try {
          Sentry.getGlobalScope().setAttributes({ device_status: device.status });
        } catch {
          // analytics must never break UX
        }

        if (uuid) {
          (window as any).__b?.('8-before-tx');
          const response = await getTransaction(
            uuid,
            transaction?.externalUserToken,
            device
          );

          if (response.data.success) {
            (window as any).__b?.('9-tx-ok');
            setTransaction(response.data.paymentRequest);
            setLoading(false);
            trackOnce(`transaction.loaded:${uuid}`, () =>
              track({
                name: 'transaction.loaded',
                payload: {
                  ...buildBasePayload(uuid, response.data.paymentRequest),
                  status: response.data.paymentRequest.status,
                },
              }),
            );
            if (response.data.paymentRequest.status === 'CLIENT_PENDING') {
              await handleProcess();
            }
          } else {
            Sentry.captureException(response.data.message);
            setError(response.data.message || 'Unknown error');
            setLoading(false);
          }
        }
      } catch (error: any) {
        console.error('Ошибка при сборе данных или получении транзакции:', error);
        Sentry.captureException(error.message);
        setError(error.message);
        setLoading(false);
      }
    };

    if (uuid) {
      collectAndFetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, retry]);

  // CPAYMENT-1716: таймер принадлежит СОСТОЯНИЮ тикета, а не тикету целиком — на реквизите и
  // экране чека это дедлайн тикета (его же применяет крон), на авто-проверке чека — дедлайн
  // самого ожидания. Оба приходят с бэкенда, локальная формула осталась только фолбэком для
  // не-матчинга (см. services/stateTimeout).
  useEffect(() => {
    // Concierge: the chat shows its own timer and never sends the client back on expiry.
    if (transaction?.concierge) return;
    const endTime = getStateDeadline(transaction);
    if (!endTime) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      return Math.max(0, Math.floor((endTime.getTime() - now.getTime()) / 1000));
    };

    const initialTimeLeft = calculateTimeLeft();
    setTimeLeft(initialTimeLeft);

    // Возврат к мерчанту на уже истёкшем таймере остаётся для всех, кому мы не покажем экран
    // таймаута: клиент не подавал признака жизни, ждать ему нечего. Клиента, который переходил
    // к чеку, редиректить нельзя — он мог уже перевести деньги и ему нужен путь в поддержку.
    // Гейт авто-проверки под это правило не попадает: там тикет живой, а ждём мы бэкенд.
    if (
      initialTimeLeft <= 0 &&
      transaction?.redirectUrl &&
      transaction?.status !== 'FILES_AUTO_VERIFICATION' &&
      !transaction?.receiptIntent
    ) {
      handleRedirect(transaction.redirectUrl);
      return;
    }

    const timerInterval = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      if (newTimeLeft <= 0) {
        clearInterval(timerInterval);
      }
    }, 1000);

    return () => clearInterval(timerInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, retry]);

  // CPAYMENT-1716: ожидание кончилось ничем — страница обязана дать клиенту следующий шаг сама.
  // Раньше здесь стоял редирект к мерчанту на уже истёкшем дедлайне: клиент, перезагрузивший
  // страницу после «далее», молча улетал обратно, хотя деньги мог уже перевести.
  useEffect(() => {
    if (transaction?.concierge) return;
    const evaluate = () => setTimeoutScreen(resolveTimeoutScreen(transaction));

    evaluate();
    const interval = setInterval(evaluate, 1000);

    return () => clearInterval(interval);
  }, [transaction]);

  // CPAYMENT-1716: клиент уже уходил грузить чек — возвращаем его на тот же экран, а не на
  // реквизит. Иначе после закрытия и повторного открытия страницы он видел шаг назад, хотя
  // тикет уже живёт по продлённому таймауту. Восстанавливаем один раз: «Назад» на экране чека
  // должен работать.
  useEffect(() => {
    if (transaction?.concierge || receiptIntentRestoredRef.current || !transaction?.receiptIntent) return;
    if (transaction.simpleConfirmation !== false) return;
    if (
      transaction.status !== 'PAYMENT_PENDING' &&
      transaction.status !== 'WAITING_FOR_SMS'
    ) {
      return;
    }

    receiptIntentRestoredRef.current = true;
    receiptIntentSentRef.current = true;
    setShowConfirm(true);
  }, [transaction]);

  // CPAYMENT-1731: попап открывается по таймеру, то есть вне пользовательского жеста — а звук в
  // такой момент браузер не пустит. Будим звук заранее, на первом же касании страницы; сам жест
  // при этом ничего не проигрывает.
  useEffect(() => {
    primeAlertSound();
  }, []);

  // CPAYMENT-1731: за 2 минуты до конца окна предупреждаем клиента, который уже мог перевести
  // деньги и просто не смотрит на таймер. Условие момента живёт в `services/lowTimeWarning`,
  // здесь — только одноразовость и звук.
  useEffect(() => {
    if (lowTimeWarnedRef.current || transaction?.concierge) return;
    if (!shouldWarnLowTime(transaction, timeLeft, showConfirm)) return;

    lowTimeWarnedRef.current = true;
    setLowTimeWarning(true);
    playAlertSound();
    track({ name: 'payment.low_time_shown', payload: basePayload });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, timeLeft, showConfirm]);

  useEffect(() => {
    if (transaction?.concierge) return;
    if (
      transaction?.status === 'WAITING_FOR_SMS' ||
      transaction?.status === 'PAYMENT_PENDING' ||
      // CPAYMENT-1616: keep polling while the receipt auto-verification gate runs, so the phase
      // (pending/failed/passed) updates and the ticket advances to FILES_VERIFICATION on its own.
      transaction?.status === 'FILES_AUTO_VERIFICATION' ||
      // A payment under review ends in a credit, a refusal or a dispute — a template that shows the
      // review has to learn which. Review lasts long, so it is read at a slower pace.
      (followsReview && transaction?.status === 'FILES_VERIFICATION')
    ) {
      const pollInterval =
        transaction?.status === 'FILES_VERIFICATION' ? REVIEW_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
      let statusCheckInterval: ReturnType<typeof setInterval> | null = null;

      const fetchStatus = async () => {
        try {
          const response = await getTransaction(uuid, transaction?.externalUserToken);
          if (response.data.success) {
            setTransaction(response?.data?.paymentRequest);
          }
        } catch (err) {
          console.error('Error checking transaction status', err);
        }
      };

      const start = () => {
        if (statusCheckInterval) return;
        statusCheckInterval = setInterval(fetchStatus, pollInterval);
      };

      const stop = () => {
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
          statusCheckInterval = null;
        }
      };

      if (!document.hidden) start();

      const handleVisibilityChange = () => {
        if (document.hidden) {
          stop();
        } else {
          fetchStatus();
          start();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        stop();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, uuid, retry, followsReview]);

  useEffect(() => {
    if (
      !uuid ||
      // A concierge client gets the payment link from staff in the chat, never by a redirect.
      transaction?.concierge ||
      transaction?.requisite?.type !== 'redirect_url' ||
      !transaction?.requisite?.paymentUrl
    ) {
      return;
    }
    // Any further transaction update within this same page load is not a new decision — the
    // navigation call below is already in flight or done. Re-running it would race the two
    // window.location.replace targets and could send the client to redirectUrl before the
    // checkout even had a chance to load (CPAYMENT-1846).
    if (paymentUrlHandledRef.current) return;
    paymentUrlHandledRef.current = true;

    const sentKey = `paymentUrlSent:${uuid}`;
    let alreadySent: string | null = null;
    try {
      alreadySent = sessionStorage.getItem(sentKey);
    } catch {}

    if (alreadySent && transaction.redirectUrl) {
      window.location.replace(transaction.redirectUrl);
      return;
    }

    try {
      sessionStorage.setItem(sentKey, '1');
    } catch {}
    window.location.replace(transaction.requisite.paymentUrl);
  }, [transaction, uuid]);

  useEffect(() => {
    const status = transaction?.status;
    if (
      status === 'COMPLETED' ||
      status === 'DECLINED' ||
      status === 'EXPIRED' ||
      status === 'FILES_VERIFICATION'
    ) {
      trackOnce(`state.final_shown:${status}`, () =>
        track({
          name: 'state.final_shown',
          payload: { ...basePayload, status },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.status]);

  // CPAYMENT-1670: while a client-requested search runs, keep the page on the search screen and,
  // when the cascade round has no adapter left (CLIENT_PENDING), re-drive it exactly like a fresh
  // page visit does (collectAndFetchData -> handleProcess) — that opens the next round.
  useEffect(() => {
    if (!retrySearching || !transaction) return;

    if (!SEARCHING_STATUSES.includes(transaction.status)) {
      setRetrySearching(false);
      retryRoundRef.current = null;
      return;
    }

    if (transaction.status === 'CLIENT_PENDING') {
      const round = transaction.paymentAdapterSearchAttemptsCounter ?? 0;
      if (retryRoundRef.current !== round) {
        retryRoundRef.current = round;
        setRetry((prev) => prev + 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, retrySearching]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleProcess = async () => {
    try {
      setLoading(true);
      const response = await processTransaction(
        uuid,
        transaction?.externalUserToken,
        transaction?.paymentTicketHistory,
        transaction?.isClientWasUnblocked,
        transaction?.isClientBlocked
      );
      if (response.status === 200) {
        setTransaction(response.data.paymentRequest);
      }
      setLoading(false);
    } catch (err) {
      Sentry.captureException({ target: 'handleProcess', err });
      setError('Error processing transaction');
      setLoading(false);
    }
  };

  const handleRedirect = (url: string) => {
    if (window.location.href !== url) {
      window.location.replace(url);
    }
  };

  const handleCancel = async (picked?: CancelReasonOption, context?: DeclineContext) => {
    const reason = isCancelReasonOption(picked) ? picked : undefined;
    // The pick is reported before the cancel and only if the client made one: no event means
    // "did not say", not "no reason".
    if (reason) {
      track({ name: 'payment.cancel_reason', payload: { ...basePayload, reason: reason.code } });
    }
    track({ name: 'payment.decline_clicked', payload: basePayload });
    markVisitAction('payment.decline_clicked');
    const revisionId = cancelReasonList?.revisionId;
    const pick =
      reason && typeof revisionId === 'number'
        ? { cancelReasonId: reason.id, cancelReasonRevisionId: revisionId }
        : undefined;
    try {
      setLoading(true);
      const response = await declineTransaction(
        uuid,
        transaction?.externalUserToken,
        transaction?.paymentTicketHistory,
        transaction?.isClientWasUnblocked,
        transaction?.isClientBlocked,
        pick,
        context
      );
      if (response.status === 200) {
        // A cancellation does not always land on DECLINED: the outcome depends on the state the
        // ticket leaves from — from a shown requisite and from waiting for an SMS it becomes
        // EXPIRED, and those are the majority. A locally assumed status would diverge from the
        // ticket in the final-state analytics event, so take the one the server returned and
        // never invent one: if the response carries no status, the state stays as it was.
        const serverStatus = response.data?.paymentRequest?.status;
        if (serverStatus) {
          setTransaction({ ...transaction, status: serverStatus });
        }
        handleRedirect(transaction.redirectUrl);
      }
    } catch (err) {
      Sentry.captureException({ target: 'handleCancel', err });
      setError('Error cancelling transaction');
      setLoading(false);
    }
  };

  /**
   * The client refuses what the page offered — an adjusted amount or the variant tiles. It is the
   * ordinary cancel; the context only tells the server what was being refused.
   */
  const handleDeclineOffer = (context: DeclineContext) => handleCancel(undefined, context);

  /**
   * CPAYMENT-1730: уход со страницы без отмены заявки. Клиент, уже отметивший «Я оплатил», мог
   * реально перевести деньги — тогда убивать тикет нельзя: его закроет чек или оператор.
   * Отмена при этом никуда не девается: передумавший клиент выбирает её отдельной кнопкой.
   */
  const handleLeave = () => {
    track({ name: 'payment.leave_clicked', payload: basePayload });
    markVisitAction('payment.leave_clicked');
    if (transaction?.redirectUrl) handleRedirect(transaction.redirectUrl);
  };

  /**
   * CPAYMENT-1616: чек может уйти на авто-проверку — оптимистичный COMPLETED показал бы
   * ложный успех и остановил опрос статуса (COMPLETED не входит в список polling).
   * Читаем реальный статус; особый случай только для гейта, остальные флоу без изменений.
   *
   * Общий хвост двух путей подтверждения — `handleConfirm` и `handleConfirmWithReceipt`.
   */
  const applyConfirmedStatus = async () => {
    const fresh = await getTransaction(
      uuid,
      transaction?.externalUserToken
    ).catch(() => null);
    const freshTransaction = fresh?.data?.success
      ? fresh.data.paymentRequest
      : null;

    // A template that follows the review shows the ticket as it is: no status is invented for it.
    // If the read failed the transaction stays as it was and the poll catches up.
    if (followsReview) {
      if (freshTransaction) setTransaction(freshTransaction);
      return;
    }

    if (freshTransaction?.status === 'FILES_AUTO_VERIFICATION') {
      setTransaction(freshTransaction);
    } else {
      setTransaction({ ...transaction, status: 'COMPLETED' });
    }
  };

  const handleConfirm = async (key?: string) => {
    track({ name: 'payment.confirm_clicked', payload: basePayload });
    markVisitAction('payment.confirm_clicked');
    intervalRef.current = null;
    try {
      setLoading(true);
      const response = await confirmTransaction(
        uuid,
        transaction?.externalUserToken,
        transaction?.paymentTicketHistory,
        transaction?.isClientWasUnblocked,
        transaction?.isClientBlocked
      );
      if (response.status === 200) {
        await applyConfirmedStatus();
      } else {
        setError('Error confirming transaction');
      }
      setLoading(false);
      setShowConfirm(false);
    } catch (err) {
      Sentry.captureException({ target: 'handleConfirm', err });
      setError('Error confirming transaction');
      setLoading(false);
    }
  };

  /**
   * 422 на «чек + подтверждение»: заявка подтверждение больше не принимает. Перечитываем её: ушла
   * дальше (истекла, на проверке) — страница сразу идёт за ней; всё ещё ждёт оплаты — отказ
   * окончательный, повторное нажатие дало бы тот же отказ, поэтому это ошибка подтверждения, как в
   * прежнем двухшаговом флоу.
   */
  const followRefusedConfirm = async () => {
    const fresh = await getTransaction(uuid, transaction?.externalUserToken).catch(() => null);
    const freshTransaction = fresh?.data?.success ? fresh.data.paymentRequest : null;

    if (freshTransaction && !['PAYMENT_PENDING', 'WAITING_FOR_SMS'].includes(freshTransaction.status)) {
      setTransaction(freshTransaction);
      return;
    }
    setError('Error confirming transaction');
  };

  /**
   * Одно действие «чек + подтверждение оплаты» для одностраничного шаблона: один запрос вместо
   * загрузки и confirm по отдельности. Неуспех ничего не меняет — клиент остаётся на экране и
   * повторяет; успех проходит тем же хвостом, что и `handleConfirm`.
   */
  const handleConfirmWithReceipt = async (
    file: File,
    handlers: ReceiptUploadHandlers
  ): Promise<ReceiptUploadResult> => {
    const result = await confirmWithReceipt(uuid ?? '', transaction, file, handlers);
    if (!result.ok) {
      if (result.httpStatus === 422) await followRefusedConfirm();
      return result;
    }

    track({ name: 'payment.confirm_clicked', payload: basePayload });
    markVisitAction('payment.confirm_clicked');
    intervalRef.current = null;
    await applyConfirmedStatus();
    setShowConfirm(false);
    return result;
  };

  /**
   * CPAYMENT-1716: единственная точка входа на экран загрузки чека — через неё проходят все
   * шаблоны и методы оплаты. Клик означает «клиент платит», хотя подтверждение оплаты придёт
   * только после загрузки файла, поэтому отметку ставим здесь, а не в handleConfirm.
   *
   * Возврат к реквизиту и повторное «далее» запроса не шлют — состояние уже учтено. Флаг
   * снимается только на сбое: иначе первая же неудачная попытка навсегда лишила бы клиента
   * продлённого таймаута, хотя на бэкенде отметки так и не появилось.
   *
   * Сбой запроса не показываем: экран чека должен открыться в любом случае, цена промаха —
   * прежний короткий таймаут тикета.
   *
   * CPAYMENT-1753: длительность окна выбирает бэкенд, страница лишь сообщает, с какого шаблона
   * пришла отметка: у одностраничного она уходит при показе экрана оплаты, поэтому окна ему
   * положено 20 минут, а не 35. Минуты на фронте не считаем и своим таймером не рисуем.
   */
  const handleShowConfirm: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    setShowConfirm(value);

    // CPAYMENT-1730: флаг страницы ставим до запроса и не снимаем на его сбое — гейт выхода
    // должен работать даже когда бэкенд об отметке так и не узнал.
    if (value === true) setReceiptIntentLocal(true);

    if (value !== true || receiptIntentSentRef.current) return;
    receiptIntentSentRef.current = true;

    markReceiptIntent(uuid, isOnePageTemplate)
      .then((res) => {
        if (res?.data?.success && res.data.paymentRequest) {
          setTransaction(res.data.paymentRequest);
        }
      })
      .catch((err) => {
        receiptIntentSentRef.current = false;
        Sentry.captureException({ target: 'markReceiptIntent', err });
      });
  };

  /**
   * CPAYMENT-1731: «Мне нужно ещё время» — это ровно «Далее»/«Я оплатил» в флоу с чеком, только
   * вызванное из попапа. Никакой отдельной ручки продления нет и заводить её нельзя: окно
   * расширяет та же отметка `receipt-intent`, которую ставит `handleShowConfirm`, и разойдись эти
   * два пути — продление начало бы зависеть от того, какой кнопкой клиент воспользовался.
   *
   * Сбой запроса здесь, как и у основной кнопки, не показываем: клиент хотел попасть на загрузку
   * чека и попадёт туда, цена промаха — прежнее короткое окно (см. `handleShowConfirm`).
   */
  const handleExtendTime = () => {
    track({ name: 'payment.low_time_extend_clicked', payload: basePayload });
    markVisitAction('payment.low_time_extend_clicked');
    setLowTimeWarning(false);
    handleShowConfirm(true);
  };

  const handleDismissLowTime = () => setLowTimeWarning(false);

  const attemptProcessAndCheckStatus = async () => {
    try {
      const response = await getTransaction(uuid, transaction?.externalUserToken);
      if (response.data.success) {
        setTransaction(response.data.paymentRequest);
      }
    } catch (err) {
      Sentry.captureException({ target: 'attemptProcessAndCheckStatus', err });
      setError('Error processing transaction');
    }
  };

  const handleSelectVariant = async (variantId: number) => {
    const variants = transaction?.variants ?? [];
    const variantIndex = variants.findIndex(
      (v: { id: number; amount: number }) => v.id === variantId,
    );
    track({
      name: 'variant.selected',
      payload: {
        ...basePayload,
        variantIndex,
        variantsCount: variants.length,
        amount: variants[variantIndex]?.amount ?? null,
      },
    });
    markVisitAction('variant.selected');
    setVariantSubmitting(true);
    try {
      const res = await selectVariant(uuid, variantId);
      if (res?.data?.success) {
        setTransaction(res.data.paymentRequest);
      }
    } catch (err) {
      Sentry.captureException({ target: 'handleSelectVariant', err });
    } finally {
      setVariantSubmitting(false);
    }
  };

  const handleRetrySearch = async () => {
    track({ name: 'payment.retry_search_clicked', payload: basePayload });
    markVisitAction('payment.retry_search_clicked');
    setRetrySearching(true);
    try {
      const res = await retrySearch(uuid);
      if (res?.data?.success) {
        setTransaction(res.data.paymentRequest);
      } else {
        setRetrySearching(false);
      }
    } catch (err) {
      Sentry.captureException({ target: 'handleRetrySearch', err });
      setRetrySearching(false);
    }
  };

  if (isConciergeChat(transaction) && uuid) {
    return <ConciergeChat uuid={uuid} transaction={transaction!} onRedirect={handleRedirect} />;
  }

  return (
    <Template
      transaction={transaction}
      loading={loading}
      error={error}
      timeLeft={timeLeft}
      timeoutScreen={timeoutScreen}
      uuid={uuid}
      showConfirm={showConfirm}
      transgranError={transgranError}
      receiptIntent={!!transaction?.receiptIntent || receiptIntentLocal}
      lowTimeWarning={lowTimeWarning}
      onExtendTime={handleExtendTime}
      onDismissLowTime={handleDismissLowTime}
      onConfirm={handleConfirm}
      onConfirmWithReceipt={handleConfirmWithReceipt}
      onCancel={handleCancel}
      onDeclineOffer={handleDeclineOffer}
      cancelReasons={cancelReasons}
      onLeave={handleLeave}
      onRedirect={handleRedirect}
      onSelectVariant={handleSelectVariant}
      variantSubmitting={variantSubmitting}
      onRetrySearch={handleRetrySearch}
      retrySearching={retrySearching}
      setRetry={setRetry}
      setShowConfirm={handleShowConfirm}
      setTransgranError={setTransgranError}
      setLoading={setLoading}
      formatTime={formatTime}
    />
  );
};

export default TransactionPage;
